import React from 'react';
import clsx from 'clsx';
import styles from './ProjectCardMenu.module.css';

/**
 * Menu action definition
 */
export interface MenuAction {
  /** Unique action identifier */
  id: string;
  /** Display label for the action */
  label: string;
  /** Material icon name */
  icon: string;
}

/**
 * Props for ProjectCardMenu component
 */
export interface ProjectCardMenuProps {
  /** Whether the menu is currently open */
  isOpen: boolean;
  /** Array of available actions */
  actions: MenuAction[];
  /** Callback to close the menu */
  onClose: () => void;
  /** Callback when an action is clicked */
  onActionClick: (actionId: string) => void;
  /** Layout variant: 'card' for dropdown, 'list' for compact inline */
  variant?: 'card' | 'list';
}

/**
 * ProjectCardMenu - Dropdown menu component for project actions
 * Supports two variants:
 * - 'card': Standard dropdown with close button at top
 * - 'list': Compact 125px height with close button inline on first item
 */
export const ProjectCardMenu: React.FC<ProjectCardMenuProps> = ({
  isOpen,
  actions,
  onClose,
  onActionClick,
  variant = 'card'
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div 
        className={clsx(
          styles.dropdownMenu,
          variant === 'list' && styles.dropdownMenuList
        )}
      >
        {/* Close button - positioned at top right for both variants */}
        <button
          className={clsx(
            styles.closeButton,
            variant === 'list' && styles.closeButtonList
          )}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close menu"
        >
          <span 
            className={clsx(
              'material-icons',
              styles.closeIcon,
              variant === 'card' && styles.closeIconCard
            )}
          >
            close
          </span>
        </button>
        
        {/* Menu action items */}
        {actions.map((action) => (
          <button
            key={action.id}
            className={clsx(
              styles.menuItem,
              variant === 'list' && styles.menuItemList
            )}
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(action.id);
            }}
            data-test={`menu-action-${action.id}`}
          >
            <span className={clsx('material-icons', styles.menuItemIcon)}>{action.icon}</span>
            <span className={styles.menuItemLabel}>{action.label}</span>
          </button>
        ))}
      </div>
    </>
  );
};
